import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Card, Stack, Button, Box, IconButton } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { Icon } from "@iconify/react";
import {
  LISTING_COLUMNS,
  LISTING_ACTIONS,
} from '../../config/module-configs/videos.config';
import ListData from '../table-elements/list-data.component';
import Iconify from '../common/iconify.component';
import {
  deleteVideoById,
  getDataList,
  getPlayableStream,
  syncVideoCDNStatus,
  toggleStatus,
} from '../../services/content.service';
import ROUTE_PATH from '../../config/routes.config';
import {
  ACTIONS,
  CONTENT_ALIAS,
  LS_ADMIN_USER_ID,
} from '../../config/const.config';
import AddEditVideos from './add-edit-videos.component';
import ConfirmPopup from '../common/confirm-popup.component';
import SnackbarInfo from '../common/snackbar-info.component';
import ViewVideos from './view-videos.component';
import VideoPreview from './videos-preview.component';
import ImagePreview from './image-preview.component';
import UploadAssets from './upload-assets.component';
import {
  checkAdminUserRole,
  getLocalValue,
} from '../../utils/local-cache.util';
import SearchList from '../table-elements/search-list.component';
import Type from '../form-elements/type.component';
import editUrl from '../../utils/url-edit.util';

const ListVideos = ({ seasonId, showId }) => {
  const columns = LISTING_COLUMNS;
  const actions = LISTING_ACTIONS;

  const {
    control,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      contentType: 'All',
    },
  });
  const contentTypeWatch = watch('contentType');
  const [adminUserRole, setAdminUserRole] = useState('');

  const renderColumns = [];
  columns.forEach((item) => {
    if (adminUserRole === 'ADMIN') {
      renderColumns.push(item);
    } else if (adminUserRole === 'CONTENT_OWNER' && item.dataKey !== 'status') {
      renderColumns.push(item);
    }
  });

  const renderActions = [];
  actions.forEach((item) => {
    if (adminUserRole === 'CONTENT_OWNER') {
      if (item.action !== 'delete') {
        renderActions.push(item);
      }
    }else {
      renderActions.push(item);
    }
  });

  const searchVideosRef = useRef(null);
  const [options, setOptions] = useState({
    page: 0,
    rowsPerPage: 10,
    totalRows: 0,
    rows: [],
    reloadCounter: 0,
    sortBy: '',
    sortOrder: '',
    q: null,
    error: false,
    loading: true,
    searchValue: '',
  });
  const [doAction, setDoAction] = useState({
    data: null,
    action: null,
  });
  const [snackbarInfo, setSnackbarInfo] = useState({
    show: false,
    type: '',
    message: '',
  });
  const [videoPlayer, setVideoPlayer] = useState({
    show: false,
    videoUrl: '',
    title: '',
  });
  const [imagePreview, setImagePreview] = useState({
    show: false,
    imageUrl: '',
    title: '',
  });

  const handlePageChange = (v) => {
    setOptions({
      ...options,
      page: v,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRowsPerPageChange = (v) => {
    setOptions({
      ...options,
      rowsPerPage: v,
      page: 0,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleSortingChange = (sortBy, sortOrder) => {
    setOptions({
      ...options,
      sortBy,
      sortOrder,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };
  const handleRefreshData = () => {
    setOptions({
      ...options,
      reloadCounter: options.reloadCounter + 1,
      loading: true,
    });
  };

  const handleFileDownload = (url, filename = '') => {
    const downloadLink = url;
    const downloadFileName = filename || url.substr(url.lastIndexOf('/') + 1);
    const a = document.createElement('a');
    a.href = downloadLink;
    a.download = `${downloadFileName}`;
    a.target = '_blank';
    a.click();
  };

  const handleClickEvent = (video, type = '') => {
    const uri = `videoID=${video.id}&contentType=${video.contentType}`;

    getPlayableStream(uri)
      .then((res) => {
        if (type) {
          handleFileDownload(res.data.downloadUrl);
        } else {
          setVideoPlayer({
            ...videoPlayer,
            show: true,
            title: video.title,
            videoUrl: res.data.videoUrl,
          });
        }
      })
      .catch(() =>
        setTimeout(() => {
          setVideoPlayer({
            ...videoPlayer,
            show: false,
            title: '',
            videoUrl: '',
          });
        }, 5000)
      );
  };

  const handleActionSelect = (selectedAction, selectedData) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });
    setDoAction({
      ...doAction,
      data: selectedData || null,
      action: selectedAction || null,
    });

    if (selectedAction === ACTIONS.DOWNLOAD.value)
      handleClickEvent(selectedData, selectedAction);
  };

  const handleActionCancel = () => {
    setDoAction({
      ...doAction,
      data: null,
      action: null,
    });

    setImagePreview({
      ...imagePreview,
      show: false,
    });
  };

  const handleActionSuccess = (message = '') => {
    if (message) {
      setSnackbarInfo({
        ...snackbarInfo,
        show: true,
        type: 'success',
        message,
      });
    }

    handleActionCancel();
    handleRefreshData();
  };

  const handleDeleteData = () => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    deleteVideoById(doAction.data.id)
      .then(() => {
        handleActionSuccess(
          `${CONTENT_ALIAS.VIDEO_SINGULAR} deleted successfully.`
        );
      })
      .catch((e) => {
        const message =
          e.response?.data?.message ||
          'Something went wrong, please try again.';

        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message,
        });
        handleActionCancel();
      });
  };

  const handleImagePreview = (rowData) => {
    setImagePreview({
      ...imagePreview,
      show: true,
      imageUrl: rowData.thumbnailUrl,
      title: rowData.title,
    });
  };

  const handleSyncCDNStatus = (contentType) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    syncVideoCDNStatus(contentType)
      .then((res) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: `${res.data?.updatedRowsCount} data updated.`,
        });

        if (res.data?.updatedRowsCount > 0) {
          setOptions({ ...options, reloadCounter: options.reloadCounter + 1 });
        }
      })
      .catch((e) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: e.response?.data?.message,
        });
      });
  };

  const handleToggleStatus = (r, value) => {
    setSnackbarInfo({
      ...snackbarInfo,
      show: false,
    });

    const payload = {
      id: r.id,
      status: value ? 0 : 1,
      type: 'video',
    };

    toggleStatus(payload)
      .then(() => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'success',
          message: 'Status updated.',
        });
      })
      .catch((e) => {
        setSnackbarInfo({
          ...snackbarInfo,
          show: true,
          type: 'error',
          message: e.response?.data?.message,
        });
      });
  };

  const handleSearchChange = (e) => {
    setOptions({
      ...options,
      searchValue: e.target.value || '',
    });
  };

  const handleSearchVideos = () => {
    if (searchVideosRef.current.value)
    {
      setOptions({
        ...options,
        q: searchVideosRef.current.value,
        reloadCounter: options.reloadCounter + 1,
        page: 0,
      });
    }
  };

  const handleClearSearch = () => {
    setOptions({
      ...options,
      searchValue: '',
      q: null,
      reloadCounter: options.reloadCounter + 1,
    });
    searchVideosRef.current.value = '';
  };

  const dataListAPICall = () => {
    const adminUserId = getLocalValue(LS_ADMIN_USER_ID);
    const getAdminRole = checkAdminUserRole();
    setAdminUserRole(getAdminRole);

    let uri = 'videos';
    
    uri += `?adminUserId=${adminUserId}`;
    uri += `&adminUserRole=${getAdminRole}`;
    uri += `&page=${options.page + 1}`;
    uri += `&perPage=${options.rowsPerPage}`;
    if (options.sortBy && options.sortOrder) {
      uri += `&sortBy=${options.sortBy}`;
      uri += `&sortOrder=${options.sortOrder}`;
    }
    if (options.q) {
      uri += `&q=${options.q}`;
    } else if (searchVideosRef.current?.value) {
      uri += `&q=${searchVideosRef.current.value}`;
    }
    if (contentTypeWatch) {
      uri += `&contentType=${contentTypeWatch}`;
    }
    else{
      uri += `&contentType=ALL`;
    }
    if (seasonId) {
      uri += `&season=${seasonId}`;
    }
    if (showId) {
      uri += `&show=${showId}`;
    }
    setOptions({ ...options, loading: true, searchValue: '' });

    getDataList(uri)
      .then((res) => {
        setOptions({
          ...options,
          loading: false,
          totalRows: res?.data?.totalRows || 0,
          rows: res?.data?.rows || [],
          seriesId: res?.data?.seriesId || null,
          seasonName: res?.data?.seasonName || null,
          seriesName: res?.data?.seriesName || null,
          showName: res?.data?.showName || null,
          error: false,
          q: null,
        });
      })
      .catch(() => {
        setOptions({
          ...options,
          loading: false,
          page: 0,
          totalRows: 0,
          rows: [],
          error: true,
          q: null,
        });
      });
  };

  useEffect(() => {
    const newOptions = options;
    newOptions.page = 0;
    setOptions({
      ...options,
      page: 0,
    });

    if (searchVideosRef.current?.value) {
      searchVideosRef.current.value = null;
    }

    dataListAPICall(newOptions);
  },[]);

  useEffect(() => {
    dataListAPICall();
  }, [options.reloadCounter, contentTypeWatch]);

  return (
    <>
    <Stack
        direction="row"
        justifyContent="left"
        alignItems="center"
        sx={{ px: 1, py: 1 }}
      >
        {options.seriesId && (
          <>
            <Link to={editUrl(ROUTE_PATH.CONTENT_SERIES_SEASONS, options.seriesId)}>
              <IconButton color="primary" variant="contained" >
                <Icon icon="material-symbols:arrow-back-ios-new" />
              </IconButton>
            </Link>
            <Box sx={{marginLeft:0.2}} fontWeight="bold" fontSize="20px">{`Episode of ${
              options.seasonName ? `- ${options.seasonName} (${options.seriesName})` : ''
            }`}</Box>
          </>
        )}
        {options.showName && (
          <>
            <Link to={ROUTE_PATH.CONTENT_SHOWS}>
              <IconButton color="primary" variant="contained" >
                <Icon icon="material-symbols:arrow-back-ios-new" />
              </IconButton>
            </Link>
            <Box sx={{marginLeft:1}} fontWeight="bold" fontSize="20px">{`Podcast of ${
              options.showName ? `- ${options.showName}` : ''
            }`}</Box>
          </>
        )}
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 1, py: 2 }}
      >
        <div>
          <Button
            color="primary"
            variant="contained"
            startIcon={<Iconify icon="ic:round-add" />}
            onClick={() => handleActionSelect(ACTIONS.CREATE.value, null)}
          >
            Create New Content
          </Button>
          <Button
            color="primary"
            variant="contained"
            startIcon={<Iconify icon="fe:sync" />}
            sx={{
              marginLeft: '5px',
            }}
            onClick={() => handleSyncCDNStatus('VIDEO')}
          >
            Sync CDN Status
          </Button>

          <Controller
            name="contentType"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Type
                id="content_type"
                name="contentType"
                label="Content Type"
                defaultValue={value}
                onChange={onChange}
                validOptions={['All','MOVIE', 'EPISODE', 'PODCAST']}
                sx={{
                  width: '200px',
                  height: '36px',
                  marginLeft: '5px',
                }}
                parentSx={{
                  width: '200px',
                  height: '36px',
                }}
                labelSx={{
                  transform:
                    'translate(14px, 8px) scale(1) !important',
                }}
                error={errors?.contentType?.message || ''}
                fullWidth={false}
              />
            )}
          />

          <SearchList
            label={CONTENT_ALIAS.VIDEO_PLURAL}
            handleSearch={handleSearchVideos}
            inputRef={searchVideosRef}
            onChange={handleSearchChange}
            options={options}
            clearSearch={handleClearSearch}
            loading={options.loading}
          />
        </div>
        <div>
          <Button
            color="inherit"
            startIcon={<Iconify icon="ic:twotone-refresh" />}
            disabled={options.loading}
            onClick={handleRefreshData}
          >
            Refresh
          </Button>
        </div>
      </Stack>

      <Card>
        <ListData
          columns={renderColumns}
          rows={options.rows}
          page={options.page}
          rowsPerPage={options.rowsPerPage}
          totalRows={options.totalRows}
          loading={options.loading}
          actions={renderActions}
          error={options.error}
          sortBy={options.sortBy}
          sortOrder={options.sortOrder}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          onSortChange={handleSortingChange}
          onAction={handleActionSelect}
          onCustomClick={handleClickEvent}
          handleImagePreview={handleImagePreview}
          handleToggleStatus={handleToggleStatus}
          adminUserRole={adminUserRole}
        />
      </Card>

      {doAction.action === ACTIONS.CREATE.value && (
        <AddEditVideos
          title={`Add New ${CONTENT_ALIAS.VIDEO_SINGULAR}/${CONTENT_ALIAS.AUDIO_SINGULAR}`}
          dataId={0}
          selectedSeason={seasonId}
          selectedShow={showId}
          seasonSeries={options.seriesId}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
          options={options}
          adminUserRole={adminUserRole}
        />
      )}

      {doAction.action === ACTIONS.EDIT.value && (
        <AddEditVideos
          title={`Edit ${doAction.data.contentType === 'PODCAST' ? CONTENT_ALIAS.AUDIO_SINGULAR : CONTENT_ALIAS.VIDEO_SINGULAR} Details - ${doAction.data.title}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
          options={options}
          adminUserRole={adminUserRole}
        />
      )}

      {doAction.action === ACTIONS.VIEW.value && (
        <ViewVideos
          title={`${doAction.data.contentType === 'PODCAST' ? CONTENT_ALIAS.AUDIO_SINGULAR : CONTENT_ALIAS.VIDEO_SINGULAR} Details - ${doAction.data.title}`}
          dataId={doAction.data.id}
          onCancel={handleActionCancel}
          adminUserRole={adminUserRole}
          type={doAction.data.contentType}
        />
      )}

      {doAction.action === ACTIONS.DELETE.value && (
        <ConfirmPopup
          title={`Delete ${CONTENT_ALIAS.VIDEO_SINGULAR}`}
          message={`Do you want to delete ${doAction.data.title} ${CONTENT_ALIAS.VIDEO_SINGULAR}? You can not undo this action!`}
          onCancel={handleActionCancel}
          onSuccess={handleDeleteData}
        />
      )}

      {doAction.action === ACTIONS.UPLOAD_ASSETS.value && (
        <UploadAssets
          title={`Upload Assets - ${doAction.data.title}`}
          dataId={doAction.data.id}
          dataTitle={doAction.data.title}
          onCancel={handleActionCancel}
          onSuccess={handleActionSuccess}
        />
      )}

      {videoPlayer.show && (
        <VideoPreview
          videoPlayer={videoPlayer}
          setVideoPlayer={setVideoPlayer}
        />
      )}

      {imagePreview.show && (
        <ImagePreview
          title={`Thumbnail - ${imagePreview.title}`}
          onCancel={handleActionCancel}
          imageUrl={imagePreview.imageUrl}
        />
      )}

      {snackbarInfo.show && (
        <SnackbarInfo type={snackbarInfo.type} message={snackbarInfo.message} />
      )}
    </>
  );
};

ListVideos.propTypes = {
  seasonId: PropTypes.number,
  showId: PropTypes.number,
};
ListVideos.defaultProps = {
  seasonId: null,
  showId: null,
};

export default ListVideos;
